import React, {useCallback, useState, useRef, useEffect, ChangeEvent} from 'react';
import {v4 as uuid} from 'uuid';
import {useSlate} from 'slate-react';
import {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {callTargetedAction} from 'client/framework';
import freeResponseActions from '../../../FreeResponseQuestion.actions';
import {Card, DropdownItem, IconButton, Label} from '@albert-io/atomic';
import {MediaModelV1} from 'resources/GeneratedModels/Media/MediaModel.v1';

import {saveImage} from '../../../FreeResponseQuestion.queries';

import {
  isBlockActive,
  areBlocksActive,
  isMarkActive,
  toggleBlock,
  toggleMark,
  insertImage,
  isLinkActive,
  withWorkaround,
  handleIndent
} from '../Utils/editor.utils';

import {LinkModal} from '../Elements/LinkElement.react';
import {ImageErrorModal} from '../Elements/ImageElement.react';

import {BlockType, MarkType} from '../slate.types';
import {WSStore} from '../../../FreeResponseQuestion.types';
import WrittenSubmissionContext from '../../WrittenSubmission.context';

// Defining types for button types that we want to render differently than a standard toggle button
const fontTypes = ['heading', 'heading-one', 'heading-two'] as const;
type FontType = typeof fontTypes[number];

type BlockButtonTypes = Exclude<BlockType, 'image' | 'link' | FontType>;

export const isFont = (type: BlockType): type is FontType => {
  return fontTypes.includes(type as FontType);
};

interface ButtonProps<T> {
  type: T;
  icon: IconProp;
  'aria-selected'?: boolean;
  tabIndex?: number;
}

export const BlockButton = ({type, icon, ...optionalProps}: ButtonProps<BlockButtonTypes>) => {
  const editor = useSlate();
  return (
    <IconButton
      icon={icon}
      color={isBlockActive(editor, type) ? 'primary' : 'secondary'}
      variant='solid'
      onClick={(event) => {
        event.preventDefault();
        withWorkaround(editor, () => toggleBlock(editor, type));
      }}
      label={type}
      {...optionalProps}
    />
  );
};

export const MarkButton = ({type, icon, ...optionalProps}: ButtonProps<MarkType>) => {
  const editor = useSlate();
  return (
    <IconButton
      icon={icon}
      variant='solid'
      color={isMarkActive(editor, type) ? 'primary' : 'secondary'}
      onClick={(event) => {
        event.preventDefault();
        withWorkaround(editor, () => toggleMark(editor, type));
      }}
      label={type}
      {...optionalProps}
    />
  );
};

export const InOutdentButton = ({
  type,
  icon,
  ...optionalProps
}: ButtonProps<'indent' | 'outdent'>) => {
  const editor = useSlate();

  return (
    <IconButton
      icon={icon}
      variant='solid'
      onClick={(event) => {
        event.preventDefault();
        withWorkaround(editor, () => handleIndent(editor, type));
      }}
      label={type}
      {...optionalProps}
    />
  );
};

/* ------ FONT STYLE DROPDOWN ---- */

type FontDropdownProps = Omit<ButtonProps<FontType>, 'icon' | 'type'>;

const maxIndex = 3;
export const FontDropdown = ({tabIndex, ...rest}: FontDropdownProps) => {
  const editor = useSlate();
  const isActive = areBlocksActive(editor, [...fontTypes]);
  const [showDropdown, setShowDropdown] = useState(false);

  return (
    <IconButton
      className='u-position_relative'
      icon='text-size'
      color={isActive ? 'primary' : 'secondary'}
      label='text-size'
      variant='solid'
      onClick={() => {
        setShowDropdown(true);
      }}
      tabIndex={showDropdown ? -1 : tabIndex}
      {...rest}
    >
      {showDropdown && <FontDropdown.Tray onClose={() => setShowDropdown(false)} />}
    </IconButton>
  );
};
type FontToText = {
  [key in FontType]: {text: string; class: string};
};

const fontTypeToAttr: FontToText = {
  heading: {text: 'Title', class: 'written-submission__title'},
  'heading-one': {text: 'Heading', class: 'written-submission__heading'},
  'heading-two': {text: 'Subheading', class: 'written-submission__subheading'}
};

FontDropdown.Tray = ({onClose}: any) => {
  const editor = useSlate();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeCursor, setActiveCursor] = useState<number | null>(0);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (dropdownRef.current) {
        if (!dropdownRef.current.contains(event.target as Node)) {
          onClose();
        }
      }
    },
    [onClose]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      if (dropdownRef.current && dropdownRef.current.contains(event.target as Node)) {
        if (event.key === 'ArrowDown') {
          setActiveCursor((oldCursor) => {
            return oldCursor! < maxIndex ? oldCursor! + 1 : 0;
          });
        } else if (event.key === 'ArrowUp') {
          setActiveCursor((oldCursor) => {
            return oldCursor! > 0 ? oldCursor! - 1 : maxIndex;
          });
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          onClose();
        } else if (event.key === 'Enter' && activeCursor !== null) {
          const selected = dropdownRef.current.children[activeCursor] as HTMLElement;
          if (selected) {
            selected.click();
          }
        }
      }
    },
    [onClose, activeCursor]
  );

  useEffect(() => {
    if (dropdownRef.current && activeCursor !== null) {
      const selected = dropdownRef.current.children[activeCursor] as HTMLElement;
      if (selected) {
        selected.focus();
      }
    }
  }, [activeCursor]);

  useEffect(() => {
    global.document.addEventListener('keydown', handleKeyPress);
    global.document.addEventListener('click', handleClick);
    return () => {
      global.document.removeEventListener('keydown', handleKeyPress);
      global.document.removeEventListener('click', handleClick);
    };
  }, [handleKeyPress, handleClick]);

  return (
    <Card className='written-submission__toolbar-dropdown ' ref={dropdownRef}>
      {fontTypes.map((type, index) => {
        const isCurrent = activeCursor === index;
        return (
          <DropdownItem
            textProps={{className: fontTypeToAttr[type].class}}
            tabIndex={isCurrent ? 0 : -1}
            as='button'
            onClick={() => {
              withWorkaround(editor, () => {
                toggleBlock(editor, type);
                onClose();
              });
            }}
          >
            {fontTypeToAttr[type].text}
          </DropdownItem>
        );
      })}
      <DropdownItem
        as='button'
        tabIndex={-1}
        onClick={() => {
          withWorkaround(editor, () => {
            toggleBlock(editor, 'paragraph');
            onClose();
          });
        }}
      >
        Body
      </DropdownItem>
    </Card>
  );
};

/* ------ ADD LINK BUTTON ---- */

interface LinkButtonProps {
  tabIndex?: number;
}

export const LinkButton = ({tabIndex}: LinkButtonProps) => {
  const editor = useSlate();
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <IconButton
        icon='link'
        label='add link'
        variant='solid'
        tabIndex={tabIndex}
        color={isLinkActive(editor) ? 'primary' : 'secondary'}
        onClick={() => {
          setShowModal(true);
        }}
      />
      {showModal && <LinkModal onClose={() => setShowModal(false)} />}
    </>
  );
};

/* ------ IMAGE UPLOAD BUTTON ---- */

const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = getBase64StringFromDataURL(reader.result as string);
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

const getBase64StringFromDataURL = (dataURL: string) =>
  dataURL.replace('data:', '').replace(/^.+,/, '');

interface ImgUploadProps {
  tabIndex?: number;
}

export const ImgUploadBtn = (props: ImgUploadProps) => {
  const {current: id} = useRef(uuid());
  const [showModal, setShowModal] = useState(false);
  const editor = useSlate();

  const uploadImage = useCallback(
    async (draftId: string, file: File | null) => {
      if (file) {
        const data = await getBase64(file);
        try {
          const response = (await saveImage({media: data}, draftId)) as MediaModelV1;
          if (response === null) {
            throw new Error('Null Response');
          }
          const mediaId = response.getId();
          insertImage(editor, mediaId);
        } catch (err) {
          // const {errors} = e.response.body; // TODO: add custom error messaging to error modal
          setShowModal(true);
        }
      } else {
        setShowModal(true);
      }
    },
    [editor]
  );

  const handleUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>, store: WSStore | null, storeName: string | null) => {
      const target = e.currentTarget;
      const file = target.files && target.files[0];

      if (store) {
        const draftId = store.getDraftId();
        if (draftId) {
          uploadImage(draftId, file);
        } else {
          callTargetedAction({
            name: freeResponseActions.SAVE_CALLBACK,
            targetStore: storeName,
            payload: (newDraftId: string) => {
              uploadImage(newDraftId, file);
            }
          });
        }
      }
    },
    [uploadImage]
  );

  return (
    <WrittenSubmissionContext.Consumer>
      {({store, storeName}) => (
        <>
          <Label
            onFocus={(e) => {
              e.target.children[0].focus();
            }}
            for={`file-input-${id}`}
            aria-label='image upload'
            {...props}
            className='u-display_flex image-input'
          >
            <IconButton
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  document.getElementById(`file-input-${id}`)?.click();
                }
              }}
              icon='image'
              color='secondary'
              variant='solid'
              onClick={() => document.getElementById(`file-input-${id}`)?.click()}
              label='add image'
              tabIndex={-1}
            />
            <input
              className='u-display_none'
              id={`file-input-${id}`}
              accept='.jpg,.gif,.png'
              type='file'
              onClick={(e) => {
                (e.target as HTMLInputElement).value = ''; // will trigger onChange regardless if same file is added
              }}
              onChange={(e) => handleUpload(e, store, storeName)}
            />
          </Label>
          {showModal && (
            <ImageErrorModal
              handleClose={() => setShowModal(false)}
              handleRetry={() => {
                setShowModal(false);
                document.getElementById(`file-input-${id}`)?.click();
              }}
            />
          )}
        </>
      )}
    </WrittenSubmissionContext.Consumer>
  );
};
